import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Users, Shield, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';

const GuideAccessManager = () => {
  const { currentUser, userRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);

  // API URL for fetching guides
  const API_BASE_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net';

   
  useEffect(() => {
    if (userRole === 'admin' || userRole === 'superadmin') {
      fetchUsers();
      fetchAllGuides();
    }
  }, [userRole]);

  const fetchUsers = async () => {
    try {
      console.log('🔍 Fetching users from Firestore...');
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = [];

      console.log('📊 Total user documents found:', usersSnapshot.size);

      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        console.log('👤 User document:', doc.id, userData);

        if (userData.role === 'editor' || userData.role === 'viewer') {
          usersData.push({
            id: doc.id,
            ...userData
          });
          console.log('✅ Added editor/viewer:', userData.email, 'Role:', userData.role);
        } else {
          console.log('❌ Skipped user (wrong role):', userData.email, 'Role:', userData.role);
        }
      });

      console.log('🎯 Final filtered users:', usersData);
      setUsers(usersData);
    } catch (error) {
      console.error('❌ Error fetching users:', error);
    }
  };

  const fetchAllGuides = async () => {
    try {
      // Get auth token for the current user (admin/superadmin)
      const token = await currentUser.getIdToken();

      const response = await fetch(`${API_BASE_URL}/getGuides`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setGuides(data.guides);
      }
    } catch (error) {
      console.error('Error fetching guides:', error);
    } finally {
      setLoading(false);
    }
  };

  const addGuideAccess = async (userId, guideId) => {
    setUpdateLoading(true);
    try {
      await updateDoc(doc(db, 'users', userId), {
        accessible_guides: arrayUnion(guideId.toString())
      });

      // Update local state
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, accessible_guides: [...(user.accessible_guides || []), guideId.toString()] }
          : user
      ));

      console.log(`Added guide ${guideId} access to user ${userId}`);
    } catch (error) {
      console.error('Error adding guide access:', error);
      alert('Failed to add guide access');
    } finally {
      setUpdateLoading(false);
    }
  };

  const removeGuideAccess = async (userId, guideId) => {
    setUpdateLoading(true);
    try {
      await updateDoc(doc(db, 'users', userId), {
        accessible_guides: arrayRemove(guideId.toString())
      });

      // Update local state
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, accessible_guides: (user.accessible_guides || []).filter(id => id !== guideId.toString()) }
          : user
      ));

      console.log(`Removed guide ${guideId} access from user ${userId}`);
    } catch (error) {
      console.error('Error removing guide access:', error);
      alert('Failed to remove guide access');
    } finally {
      setUpdateLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (userRole !== 'admin' && userRole !== 'superadmin') {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 text-red-600">
          <Shield className="w-5 h-5" />
          <span>Access denied. Only admins can manage guide access.</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-xl font-semibold mb-4">Guide Access Management</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Guide Access Management</h3>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="w-4 h-4" />
          <span>{filteredUsers.length} editors/viewers</span>
        </div>
      </div>

      {/* Search Users */}
      <div className="mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers.map((user) => (
          <div
            key={user.id}
            className="border border-gray-200 rounded-lg p-4"
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="font-medium">{user.name}</h4>
                <p className="text-sm text-gray-600">{user.email}</p>
                <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                  user.role === 'editor' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                }`}>
                  {user.role}
                </span>
              </div>
              <button
                onClick={() => setSelectedUser(selectedUser === user.id ? null : user.id)}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
              >
                {selectedUser === user.id ? 'Hide' : 'Manage Access'}
              </button>
            </div>

            {/* Current Access */}
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Current Access ({(user.accessible_guides || []).length} guides):
              </p>
              <div className="flex flex-wrap gap-2">
                {(user.accessible_guides || []).map((guideId) => {
                  const guide = guides.find(g => g.ID.toString() === guideId);
                  return (
                    <div
                      key={guideId}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                    >
                      <span>{guide ? guide.Name : `Guide ${guideId}`}</span>
                      <button
                        onClick={() => removeGuideAccess(user.id, guideId)}
                        disabled={updateLoading}
                        className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                {(user.accessible_guides || []).length === 0 && (
                  <span className="text-sm text-gray-500">No guides assigned</span>
                )}
              </div>
            </div>

            {/* Add Access Controls */}
            {selectedUser === user.id && (
              <div className="border-t pt-3 mt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Add Guide Access:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                  {guides
                    .filter(guide => !(user.accessible_guides || []).includes(guide.ID.toString()))
                    .map((guide) => (
                      <button
                        key={guide.ID}
                        onClick={() => addGuideAccess(user.id, guide.ID)}
                        disabled={updateLoading}
                        className="flex items-center gap-2 p-2 text-left text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Plus className="w-3 h-3 text-green-600" />
                        <div>
                          <div className="font-medium">{guide.Name}</div>
                          <div className="text-xs text-gray-500">ID: {guide.ID}</div>
                        </div>
                      </button>
                    ))}
                </div>
                {guides.filter(guide => !(user.accessible_guides || []).includes(guide.ID.toString())).length === 0 && (
                  <p className="text-sm text-gray-500">User has access to all available guides</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {searchTerm ? 'No users found matching your search.' : 'No editors or viewers found.'}
        </div>
      )}
    </div>
  );
};

export default GuideAccessManager;